<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useProductsStore } from '@/stores/useProductsStore';

import TheCard from '../mode-list/TheCard.vue';

const props = defineProps<{
  isLoadingMapMovement?: boolean;
  hasLastValidResults?: boolean;
}>();

const emit = defineEmits(['hoverProduct', 'showLastResults', 'resetAll']);

const { t } = useI18n();

const globalConfigurationStore = useGlobalConfigurationStore();
const { globalConfiguration } = storeToRefs(globalConfigurationStore);

const productsStore = useProductsStore();
const { products } = storeToRefs(productsStore);

const prefixUrl = computed(() => globalConfiguration.value?.prefixUrl ?? null);

const listContainer = ref<HTMLElement>();

watch(
  () => products.value,
  () => {
    if (listContainer.value) {
      listContainer.value.scrollTop = 0;
    }
  },
);

function handleMouseOver(productId: any) {
  emit('hoverProduct', productId);
}

function handleMouseLeave() {
  emit('hoverProduct', null);
}
</script>

<template>
  <div ref="listContainer" class="list-container">
    <div v-if="products.length === 0 && !props.isLoadingMapMovement" class="no-results">
      <h3>{{ t('map.no_exact_match') }}</h3>
      <div class="no-results-buttons">
        <button class="reset-all-button" @click="$emit('resetAll')">
          {{ t('map.reset_all') }}
        </button>
        <button v-if="props.hasLastValidResults" class="last-results-button" @click="$emit('showLastResults')">
          {{ t('map.show_closest_results') }}
        </button>
      </div>
    </div>
    <div v-else class="list-wrapper">
      <TheCard
        v-for="product in products"
        :key="product.id"
        :product="product"
        :responsive-row="false"
        :small="true"
        :prefix-url="prefixUrl"
        @mouseover="handleMouseOver(product.id)"
        @focusin="handleMouseOver(product.id)"
        @mouseleave="handleMouseLeave"
        @focusout="handleMouseLeave"
      />
    </div>
  </div>
</template>

<style lang='css' scoped>
.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--yb-spacing-xl);
  text-align: center;
  height: 90%;
}

.no-results-buttons {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: var(--yb-spacing-sm);
  margin-top: var(--yb-spacing-base);
}

.last-results-button,
.reset-all-button {
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  border: none;
  border-radius: var(--yb-radius-sm);
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-semi-bold);
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: 2px solid var(--yb-color-primary);
    outline-offset: 2px;
  }
}

.last-results-button {
  background-color: var(--yb-color-primary);
  color: var(--yb-color-white);

  &:hover {
    background-color: var(--yb-color-primary-hover);
  }
}

.reset-all-button {
  background-color: var(--yb-color-white);
  color: var(--yb-color-primary);
  border: 2px solid var(--yb-color-primary);
  width: fit-content;

  &:hover {
    background-color: var(--yb-color-primary);
    color: var(--yb-color-white);
  }
}

.list-container {
  position: relative;
  padding: var(--yb-spacing-base) var(--yb-spacing-base);
  height: 100%;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  /* Hide scrollbar for Chrome, Safari and Opera */

  &::-webkit-scrollbar {
    display: none;
  }
}

.list-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-sm);
}
</style>
